import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FinanceTabs, FinanceHeader, money } from "./financeShell";

const STAGES = [
  ["estimated", "Estimated", "Calculator", "bg-slate-100 text-slate-600"],
  ["proposed", "Proposed", "FileText", "bg-blue-100 text-blue-700"],
  ["contracted", "Contracted", "PenTool", "bg-indigo-100 text-indigo-700"],
  ["invoiced", "Invoiced", "Receipt", "bg-violet-100 text-violet-700"],
  ["collected", "Collected", "Banknote", "bg-cyan-100 text-cyan-700"],
  ["recognized", "Recognized", "CheckCircle2", "bg-emerald-100 text-emerald-700"],
];

export default function RevenuePipeline() {
  const { user } = useAuth();
  const [pipe, setPipe] = useState(null);
  const [reports, setReports] = useState(null);
  useEffect(() => {
    api.get("/finance/pipeline").then((r) => setPipe(r.data.pipeline)).catch(() => setPipe({}));
    api.get("/finance/reports").then((r) => setReports(r.data)).catch(() => setReports({}));
  }, []);
  const max = pipe ? Math.max(...STAGES.map(([k]) => pipe[k] || 0), 1) : 1;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="finance-pipeline">
      <FinanceHeader title="Revenue Pipeline" subtitle="From estimate to recognized revenue across the firm." />
      <FinanceTabs user={user} />

      {!pipe ? <div className="py-20 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623] inline" /></div> : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="pipeline-funnel">
            {STAGES.map(([k, l, icon, tone]) => {
              const Icon = Icons[icon];
              const v = pipe[k] || 0;
              return (
                <div key={k} className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tone}`}><Icon className="h-4.5 w-4.5" /></div>
                  <div className="w-32 text-sm text-slate-600">{l}</div>
                  <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#F5A623] to-[#e0951a] rounded-full transition-all" style={{ width: `${Math.max(4, (v / max) * 100)}%` }} />
                  </div>
                  <div className="w-28 text-right font-semibold text-slate-800">{money(v)}</div>
                </div>
              );
            })}
          </div>

          {reports && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="pipeline-metrics">
                <h2 className="font-semibold text-slate-800 mb-3">Key Metrics</h2>
                <div className="grid grid-cols-2 gap-3">
                  {[["Collection Rate", `${reports.collection_rate ?? 0}%`], ["Estimate Conversion", `${reports.proposal_conversion ?? 0}%`],
                    ["Average Invoice", money(reports.average_invoice)], ["Invoices", reports.invoice_count ?? 0]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                      <div className="text-[11px] text-slate-400 uppercase tracking-wide">{l}</div>
                      <div className="text-lg font-bold text-slate-800 mt-1">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="pipeline-by-service">
                <h2 className="font-semibold text-slate-800 mb-3">Revenue by Service</h2>
                {Object.keys(reports.revenue_by_service || {}).length === 0 ? <p className="text-sm text-slate-400">No invoiced services yet.</p> :
                  <div className="space-y-2">
                    {Object.entries(reports.revenue_by_service).sort((a, b) => b[1] - a[1]).map(([name, amt]) => (
                      <div key={name} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 truncate pr-3">{name}</span>
                        <span className="font-semibold text-slate-800">{money(amt)}</span>
                      </div>
                    ))}
                  </div>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
